export function utcDateBounds(start: string, end: string): { start?: string; end?: string } {
  const first = start ? new Date(`${start}T00:00:00Z`) : null;
  const last = end ? new Date(`${end}T00:00:00Z`) : null;
  if ((first && !Number.isFinite(first.getTime())) || (last && !Number.isFinite(last.getTime()))) throw new Error('Choose valid dates.');
  if (first && last && first > last) throw new Error('Start date must be on or before end date.');
  if (last) last.setUTCDate(last.getUTCDate() + 1);
  return { start: first?.toISOString(), end: last?.toISOString() };
}

export function money(value: unknown, digits = 2): string {
  if (value === null || value === undefined || value === '') return 'Not recorded';
  const number = Number(value);
  return Number.isFinite(number) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(number) : 'Not recorded';
}

export function humanSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return 'Unknown';
  let size = bytes;
  for (const unit of ['B', 'KB', 'MB', 'GB']) {
    if (size < 1024 || unit === 'GB') return unit === 'B' ? `${Math.round(size)} B` : `${size.toFixed(1)} ${unit}`;
    size /= 1024;
  }
  return `${size.toFixed(1)} GB`;
}

export function jobCost(result: Record<string, unknown> | null | undefined): unknown {
  return result?.total_cost ?? result?.total_cost_for_job ?? result?.purchase_cost;
}

export function timestamp(value: string | null | undefined): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString() : 'Not recorded';
}

// Recent activity reads better as elapsed time; older entries keep the absolute timestamp.
const MINUTE = 60_000, HOUR = 60 * MINUTE, DAY = 24 * HOUR;

export function relativeTime(value: string | null | undefined): string {
  if (!value) return 'Not recorded';
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return 'Not recorded';
  const elapsed = Date.now() - time;
  if (elapsed < 0 || elapsed >= DAY) return timestamp(value);
  if (elapsed < MINUTE) return 'Just now';
  if (elapsed < HOUR) {
    const minutes = Math.floor(elapsed / MINUTE);
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  const hours = Math.floor(elapsed / HOUR);
  return `${hours} hour${hours === 1 ? '' : 's'} ago`;
}

export const JOB_TYPES = ['contacts_unlock', 'accounts_unlock', 'export', 'enrichment_export',
  'company_enrichment_export', 'add_to_list', 'remove_from_list', 'delete_contacts',
  'update_contacts', 'remove_from_account_list', 'delete_accounts'] as const;

export interface AdminJob {
  job_id: string; owner_id: string; user_name?: string | null; job_type: string;
  status: string; progress: number; created_at: string; started_at?: string | null;
  completed_at?: string | null; error?: string | null; result?: Record<string, unknown> | null;
  params?: Record<string, unknown>; display_note?: string;
}
export interface AdminJobPage { jobs: AdminJob[]; total: number; page: number; size: number; total_pages: number }
export interface UserReference { key?: string; hash?: string }

export function validAdminResponse(path: string, value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const body = value as Record<string, unknown>;
  if (path.startsWith('/api/admin/analytics/')) return !!body.overall_summary && Array.isArray(body.top_users_by_requests) && Array.isArray(body.top_endpoints_by_requests);
  if (path.startsWith('/api/admin/spend')) {
    const windows = body.windows;
    if (!windows || typeof windows !== 'object' || Array.isArray(windows)) return false;
    const known = ['1h', '12h', '24h', '48h', '7d', '30d', 'mtd'];
    if (!known.every(key => key in windows)) return false;
    const validWindow = (window: unknown) => !!window && typeof window === 'object' && !Array.isArray(window)
      && ['spend', 'events', 'paying_users', 'contacts_recorded'].every(k => typeof (window as Record<string, unknown>)[k] === 'number' && Number.isFinite((window as Record<string, number>)[k] as number));
    if (!known.every(key => validWindow((windows as Record<string, unknown>)[key]))) return false;
    if (!Array.isArray(body.top_users)) return false;
    if (!(body.top_users as unknown[]).every(row => !!row && typeof row === 'object' && !Array.isArray(row)
      && ['user_id', 'spend', 'events'].every(k => typeof (row as Record<string, unknown>)[k] !== 'undefined'))) return false;
    return typeof body.lifetime_spend === 'number' && typeof body.lifetime_events === 'number';
  }
  const collection = path.startsWith('/api/admin/logs/') ? 'logs' : path.startsWith('/api/admin/users/search') ? 'users' : path.startsWith('/api/admin/jobs?') ? 'jobs' : null;
  if (collection) return Array.isArray(body[collection]) && (body[collection] as unknown[]).every(row => !!row && typeof row === 'object' && !Array.isArray(row)) && ['total','page','size','total_pages'].every(key => typeof body[key] === 'number' && Number.isFinite(body[key]));
  if (path.startsWith('/api/admin/jobs/')) return typeof body.job_id === 'string' && typeof body.owner_id === 'string' && typeof body.job_type === 'string';
  return typeof body.api_key === 'string' && typeof body.name === 'string' && Array.isArray(body.permissions);
}
